import { createContext, useContext, useMemo, useState, type ReactNode } from 'react'

export type EnrollmentNavigationState = Record<string, unknown> & {
  appointmentScheduled?: boolean
  callRequest?: boolean
  completedAdditionalInfo?: boolean
  licenseStateMismatch?: boolean
  limitToStep?: number
  noLicense?: boolean
  returnTo?: string
  sponsoredEnrollPath?: string
  sponsoredEnrollSearch?: string
  sponsoredEnrollState?: Record<string, unknown> | null
  startStep?: number
  under18?: boolean
}

type LocalEnrollmentLocation = {
  params: Record<string, string>
  pathname: string
  searchStr: string
  state: EnrollmentNavigationState | null
}

type LocalEnrollmentNavigationContextValue = {
  canHandlePath: (to: string) => boolean
  location: LocalEnrollmentLocation
  navigate: (
    to: string,
    options?: { replace?: boolean; state?: EnrollmentNavigationState }
  ) => void
}

const LocalEnrollmentNavigationContext =
  createContext<LocalEnrollmentNavigationContextValue | null>(null)

function parseNavigationTarget(to: string) {
  const url = new URL(to, 'http://localhost')

  return {
    pathname: url.pathname,
    searchStr: url.search
  }
}

type LocalEnrollmentNavigationProviderProps = {
  canHandlePath: (pathname: string) => boolean
  children: ReactNode
  initialLocation: Partial<LocalEnrollmentLocation>
}

export function LocalEnrollmentNavigationProvider({
  canHandlePath,
  children,
  initialLocation
}: LocalEnrollmentNavigationProviderProps) {
  const [location, setLocation] = useState<LocalEnrollmentLocation>({
    params: initialLocation.params ?? {},
    pathname: initialLocation.pathname ?? '/',
    searchStr: initialLocation.searchStr ?? '',
    state: (initialLocation.state as EnrollmentNavigationState | null | undefined) ?? null
  })

  const value = useMemo<LocalEnrollmentNavigationContextValue>(
    () => ({
      canHandlePath: (to: string) => canHandlePath(parseNavigationTarget(to).pathname),
      location,
      navigate: (to, options = {}) => {
        const { pathname, searchStr } = parseNavigationTarget(to)

        setLocation(previous => ({
          params: {},
          pathname,
          searchStr,
          state: options.state
            ? ({
                ...(previous.state ?? {}),
                ...options.state
              } as EnrollmentNavigationState)
            : previous.state
        }))

        if (typeof window !== 'undefined') {
          window.requestAnimationFrame(() => {
            window.scrollTo({ top: 0, behavior: 'auto' })
          })
        }
      }
    }),
    [canHandlePath, location]
  )

  return (
    <LocalEnrollmentNavigationContext.Provider value={value}>
      {children}
    </LocalEnrollmentNavigationContext.Provider>
  )
}

export function useLocalEnrollmentNavigation() {
  return useContext(LocalEnrollmentNavigationContext)
}
